import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { LogService } from './log.service';
import { ShareService } from 'src/app/services/share.service';
import { apiLink } from '../helpers/app-constants';
import { transformObservableToSinglePromise } from '../helpers/angular-helpers';
import { UnpublishOrDeletePublishesFromEventListsQuery, PublishedEventListsData, UnpublishOrDeleteEventListsQuery, EventListPublish, ProgramPublishCalendarData, UpdateEventListWithPublishesQuery, EventListInfo } from 'globals/store/share.event-lists.data';
import { eventListIdsByPublishIdsReselector } from '../store/reselectors/event-lists.reselector';
import { PublishInfo } from 'globals/store/share.data';
import { PublicEventData } from 'globals/store/public.model';
import { extractSingleEventListPublishCalendarData, programPublishToEventListPublish } from '../store/share.event-lists.functions';
import { environment } from 'src/environments/environment';
import { lastValueFrom, take } from 'rxjs';
import { Account } from 'globals/store/accounts.data';
import { accountsFeature } from '../store/accounts.era';

@Injectable({ providedIn: 'root' })
export class ShareEventListService {
  readonly _file = 'share.event-lists.service.ts'
  constructor(
    private http:HttpClient,
    private log:LogService,
    private shareService: ShareService,
    public store: Store,
  ) {}

  async getEventListIdsByPublishIdsReselector(): Promise<{ [id:string]: Set<string> }> {
    const eventListIdsByPublishIds: { [id:string]: Set<string> } = await lastValueFrom(this.store.select(eventListIdsByPublishIdsReselector).pipe(take(1)))
    return eventListIdsByPublishIds
  }

  private async getEventListIdsToPublishIds(publishCodes: string[]): Promise<{ eventListId: string, publishCodes: string[]}[]> {
    const eventListIdsByPublishIds = await this.getEventListIdsByPublishIdsReselector()
    const publishIdsByEventListId: { [id:string]: string[] } = {}
    publishCodes.forEach(publishCode => {
      const eventListIds = eventListIdsByPublishIds[publishCode]
      if (!eventListIds) return
      eventListIds.forEach(id => {
        if (!publishIdsByEventListId[id]) publishIdsByEventListId[id] = []
        publishIdsByEventListId[id].push(publishCode)
      })
    })
    return Object.entries(publishIdsByEventListId).map(([eventListId, publishCodes]) => ({ eventListId, publishCodes }))
  }

  async updateEventListsWithPublishViews(publishesAndInfos: { eventData: PublicEventData, publishInfo: PublishInfo }[]) {
    const publishCodesByEventListIdList = await this.getEventListIdsToPublishIds(publishesAndInfos.map(({ publishInfo: i }) => i.publishCode))

    const account: Partial<Account> = await lastValueFrom(this.store.select(accountsFeature.selectAccount).pipe(take(1)))
    const eventListInfos: { [id: string]: EventListInfo } = account.eventListInfos || {}  
    const promise = Promise.all(publishCodesByEventListIdList.map(({ eventListId, publishCodes }) => {
      const eventListInfo = eventListInfos[eventListId]
      const updatedPublishes: { [id:string]: EventListPublish } = {}
      const updatedCalendarData: { [id: string]: ProgramPublishCalendarData } = {}
      publishesAndInfos.filter(pAndA => publishCodes.includes(pAndA.publishInfo.publishCode)).forEach(({ eventData, publishInfo }) => {
        updatedPublishes[publishInfo.publishCode] = programPublishToEventListPublish(publishInfo.publishCode, eventData, eventListInfo.excludedFieldIds, environment.joi.shareBase)
        updatedCalendarData[publishInfo.publishCode] = extractSingleEventListPublishCalendarData(eventData, publishInfo, eventListInfo.excludedFieldIds)
      })
      const query: UpdateEventListWithPublishesQuery = {
        accountId: this.shareService.accountId,
        eventListId,
        publishCodes,
        updatedPublishes,
        updatedCalendarData,
      }
      console.log({ query })
      return transformObservableToSinglePromise(this.http.post(`${apiLink}/update-event-list-with-publish-views`, query))
    }))
    await this.log.executePromiseLogError(this, promise, 'updateEventListsWithPublishViews', `There was an error updating event lists with publish views`)
  }

  
  async unpublishPublishViewFromEventLists(publishCode: string) {
    const eventListIdsByPublishIds = await this.getEventListIdsByPublishIdsReselector()
    const ids = eventListIdsByPublishIds[publishCode]
    console.log({ publishCode, eventListIdsByPublishIds, ids })
    if (ids && ids.size) {
      const promise = Promise.all(Array.from(ids).map(id => this.hideOrDeletePublishViewsFromEventList(id, [publishCode], false)))
      const noError = await this.log.executePromiseLogError(this, promise, 'unpublishPublishView hidePublishViews', `There was an error hiding publish views in an event list`)
      return noError
    } else return false
  }

  async deletePublishViewsFromEventLists(publishInfos: PublishInfo[]) {
    const eventListPublishCodesList = await this.getEventListIdsToPublishIds(publishInfos.map(i => i.publishCode))
    const promise = Promise.all(eventListPublishCodesList.map(({ eventListId, publishCodes }) => 
      this.hideOrDeletePublishViewsFromEventList(eventListId, publishCodes, true)
    ))
    return await this.log.executePromiseLogError(this, promise, 'deletePublishViews from EventList', `There was an error deleting publish views from an event list`)
  }

  private async hideOrDeletePublishViewsFromEventList(eventListId: string, publishCodes: string[], permanent: boolean) {
    const query: UnpublishOrDeletePublishesFromEventListsQuery = { eventListId, publishCodes, permanent, accountId: this.shareService.accountId } 
    console.log(query)
    await transformObservableToSinglePromise(this.http.post(`${apiLink}/hide-or-delete-publish-views-from-event-list`, query))
  }


  async publishOrUpdateEventList(data: PublishedEventListsData) {
    const query = { data, date: new Date().getTime() }
    const promise = transformObservableToSinglePromise(this.http.post(`${apiLink}/save-event-list`, query))
    await this.log.executePromiseLogError(this, promise, 'publishOrUpdateEventList', `There was an error publishing the event list`)
  }

  async unpublishOrDeleteEventList(query: UnpublishOrDeleteEventListsQuery) {
    const promise = transformObservableToSinglePromise(this.http.post(`${apiLink}/delete-or-unpublish-event-list`, query))
    await this.log.executePromiseLogError(this, promise, 'unpublishOrDeleteEventList', `There was an error ${query.command}ing the event list`)
  }

  async downloadEventList(id: string) {
    console.log({ id })
    const promise = transformObservableToSinglePromise<PublishedEventListsData>(this.http.post(`${apiLink}/get-event-list`, { id }))
    return await this.log.executePromiseLogError(this, promise, 'downloadEventList', `There was an error getting the event list`) as PublishedEventListsData
  }

}